import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LogIn, LogOut, Home, FileText, Upload, MessageSquare, 
  Clock, CheckCircle, AlertTriangle, IndianRupee, Eye,
  Send, Paperclip, Download, Building2, User, Mail, Phone,
  Receipt
} from 'lucide-react';
import ClientBusinessInvoicing from './ClientBusinessInvoicing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/client-portal`;

const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('clientPortalToken'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Login state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ category: 'other', description: '', file: null });
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const axiosConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Check if already logged in
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setIsLoggedIn(true);
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('clientPortalToken');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('clientPortalToken', access_token);
      setToken(access_token);
      setUser(userData);
      setIsLoggedIn(true);
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clientPortalToken');
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
    setDashboardData(null);
    setInvoices([]);
    setDocuments([]);
    setMessages([]);
  };

  // Fetch data based on active tab
  useEffect(() => {
    if (isLoggedIn && token) {
      if (activeTab === 'dashboard') fetchDashboard();
      else if (activeTab === 'invoices') fetchInvoices();
      else if (activeTab === 'documents') fetchDocuments();
      else if (activeTab === 'messages') fetchMessages();
    }
  }, [activeTab, isLoggedIn, token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, axiosConfig());
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, axiosConfig());
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, axiosConfig());
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, axiosConfig());
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return;
    
    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('category', uploadForm.category);
    formData.append('description', uploadForm.description);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadModalOpen(false);
      setUploadForm({ category: 'other', description: '', file: null });
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, { message: newMessage }, axiosConfig());
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Client Portal</CardTitle>
            <p className="text-gray-400 mt-2">Access your compliance dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-lg text-sm">
                  {loginError}
                </div>
              )}
              
              <div>
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="your@email.com"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  required
                  data-testid="portal-email-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Password</Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="Enter your password"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  required
                  data-testid="portal-password-input"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                disabled={loginLoading}
                data-testid="portal-login-btn"
              >
                {loginLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
            
            <p className="text-center text-gray-500 text-sm mt-6">
              Contact your CA/firm if you don't have login credentials
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged In Portal
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">{user?.firm_name || 'Client Portal'}</h1>
              <p className="text-sm text-gray-500">Welcome, {user?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="portal-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="tab-dashboard">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="business" className="flex items-center gap-2" data-testid="tab-business">
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">My Business</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="tab-invoices">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">CA Invoices</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="tab-documents">
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="tab-messages">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Messages</span>
              {dashboardData?.messages?.unread_count > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {dashboardData.messages.unread_count}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" data-testid="dashboard-content">
            <div className="space-y-6">
              {/* Compliance Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-80" />
                    <div className="text-2xl font-bold">{dashboardData?.compliance?.pending || 0}</div>
                    <div className="text-xs opacity-80">Pending Tasks</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-80" />
                    <div className="text-2xl font-bold">{dashboardData?.compliance?.overdue || 0}</div>
                    <div className="text-xs opacity-80">Overdue</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
                    <div className="text-2xl font-bold">{dashboardData?.compliance?.completed || 0}</div>
                    <div className="text-xs opacity-80">Completed</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white border-0">
                  <CardContent className="p-4 text-center">
                    <IndianRupee className="w-8 h-8 mx-auto mb-2 opacity-80" />
                    <div className="text-2xl font-bold">₹{(dashboardData?.invoices?.total_outstanding || 0).toLocaleString()}</div>
                    <div className="text-xs opacity-80">Outstanding</div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Tasks */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Compliance Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData?.compliance?.recent_tasks?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.compliance.recent_tasks.map((task) => (
                        <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{task.title}</div>
                            <div className="text-sm text-gray-500">Due: {task.due_date}</div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            task.status === 'completed' ? 'bg-green-100 text-green-800' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {task.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent tasks</p>
                  )}
                </CardContent>
              </Card>

              {/* Invoice Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Invoice Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        ₹{(dashboardData?.invoices?.total_invoiced || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Total Invoiced</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        ₹{(dashboardData?.invoices?.total_paid || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Total Paid</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">
                        ₹{(dashboardData?.invoices?.total_outstanding || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Outstanding</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Business Invoicing Tab */}
          <TabsContent value="business" data-testid="business-content">
            <ClientBusinessInvoicing token={token} />
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" data-testid="invoices-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div>
                          <div className="font-medium text-gray-900">{invoice.invoice_number}</div>
                          <div className="text-sm text-gray-500">{invoice.invoice_date}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-900">₹{invoice.total_amount?.toLocaleString()}</div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {invoice.status}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedInvoice(invoice)}
                          data-testid={`view-invoice-${invoice.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No invoices found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" data-testid="documents-content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Documents</CardTitle>
                <Button
                  onClick={() => setUploadModalOpen(true)}
                  className="bg-gradient-to-r from-orange-500 to-red-500"
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{doc.file_name}</div>
                            <div className="text-sm text-gray-500">
                              {doc.category} • {new Date(doc.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Download className="w-5 h-5" />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No documents uploaded yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" data-testid="messages-content">
            <Card className="h-[600px] flex flex-col">
              <CardHeader>
                <CardTitle className="text-lg">Messages with {user?.firm_name}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                {/* Messages List */}
                <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                  {messages.length > 0 ? (
                    [...messages].reverse().map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] p-3 rounded-lg ${
                          msg.sender_type === 'client'
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <div className="text-sm font-medium mb-1">
                            {msg.sender_name}
                          </div>
                          <div>{msg.message}</div>
                          <div className={`text-xs mt-1 ${
                            msg.sender_type === 'client' ? 'text-orange-200' : 'text-gray-500'
                          }`}>
                            {new Date(msg.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">No messages yet. Start a conversation!</p>
                  )}
                </div>

                {/* Message Input */}
                <div className="flex gap-2 border-t pt-4">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    data-testid="message-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-gradient-to-r from-orange-500 to-red-500"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Upload Document Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Share documents with your CA/firm</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadDocument} className="space-y-4">
            <div>
              <Label>Category</Label>
              <select
                value={uploadForm.category}
                onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                className="w-full mt-1 p-2 border rounded-md"
              >
                <option value="gst_returns">GST Returns</option>
                <option value="invoices">Invoices</option>
                <option value="bank_statements">Bank Statements</option>
                <option value="tds_certificates">TDS Certificates</option>
                <option value="itr_documents">ITR Documents</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of the document"
              />
            </div>
            <div>
              <Label>File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Max 10MB. PDF, Images, Excel, Word files allowed.</p>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setUploadModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={uploadLoading || !uploadForm.file} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500">
                {uploadLoading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Modal */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Invoice Number</Label>
                  <div className="font-semibold">{selectedInvoice.invoice_number}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Date</Label>
                  <div className="font-semibold">{selectedInvoice.invoice_date}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Due Date</Label>
                  <div className="font-semibold">{selectedInvoice.due_date}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedInvoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedInvoice.status}
                  </span>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg">
                  <span>Subtotal</span>
                  <span>₹{selectedInvoice.sub_total?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>GST</span>
                  <span>₹{selectedInvoice.gst_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xl font-bold mt-2 pt-2 border-t">
                  <span>Total</span>
                  <span>₹{selectedInvoice.total_amount?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortal;
